import React, { useMemo } from 'react';
import Navigation from 'src/components/Navigation';
import { PATHS } from 'src/common/constants';
import { SidebarPermissionsData } from '../ModernSidebar';
import { sharedBottomLinks } from '../Constants';
import SidebarContextTree, {
  SidebarContextRow,
} from '../Components/SidebarContextTree';
import useAnnouncementContextRows from '../Hooks/useAnnouncementContextRows';
import useKeyContactContextRows from '../Hooks/useKeyContactContextRows';
import '../ModernSidebar.scss';

interface RealtorSidebarNavProps {
  collapseSideBar: boolean;
  permissions: SidebarPermissionsData;
}

const CONTEXT_ANCHOR_ORDER: string[] = ['keyContacts', 'announcements'];

const RealtorSidebarNav = ({
  collapseSideBar,
  permissions,
}: RealtorSidebarNavProps) => {
  const { allowedTabList } = permissions;

  const showAnnouncements: boolean = allowedTabList.some(
    (tab: any) => tab.name === 'Announcements'
  );

  const topLinks: any = useMemo(
    () =>
      [
        {
          id: 'dashboard',
          label: 'Dashboard',
          path: PATHS.DASHBOARD,
          icon: 'th-large',
        },
        { id: 'home', label: 'My Home', path: PATHS.SUITES_VIEW, icon: 'home' },
        showAnnouncements && {
          id: 'announcements',
          label: 'Announcements',
          path: PATHS.ANNOUNCEMENTS,
          icon: 'bullhorn',
        },
        {
          id: 'keyContacts',
          label: 'Key Contacts',
          path: PATHS.KEY_CONTACTS_LIST,
          icon: 'address-book',
        },
        { id: 'inbox', label: 'Inbox', path: PATHS.INBOX, icon: 'folder-open' },
      ].filter(Boolean),
    [showAnnouncements]
  );

  const rowsByAnchor: Record<string, SidebarContextRow[] | null> = {
    keyContacts: useKeyContactContextRows(),
    announcements: useAnnouncementContextRows(),
  };

  const activeAnchorId: string | null =
    CONTEXT_ANCHOR_ORDER.find((anchorId: string) => rowsByAnchor[anchorId]) ??
    null;

  const anchorIndex: number = activeAnchorId
    ? topLinks.findIndex((link: any) => link.id === activeAnchorId)
    : -1;

  const activeRows: SidebarContextRow[] | null = activeAnchorId
    ? rowsByAnchor[activeAnchorId]
    : null;

  if (!activeRows || anchorIndex === -1 || collapseSideBar) {
    return (
      <>
        <Navigation links={topLinks} collapseSideBar={collapseSideBar} />
        <div className="sidebar-spacer" />
        <Navigation
          links={sharedBottomLinks}
          collapseSideBar={collapseSideBar}
        />
      </>
    );
  }

  const linksThroughAnchor: any[] = topLinks.slice(0, anchorIndex + 1);

  const linksAfterAnchor: any[] = topLinks.slice(anchorIndex + 1);

  return (
    <>
      <Navigation
        links={linksThroughAnchor}
        collapseSideBar={collapseSideBar}
      />
      <SidebarContextTree rows={activeRows} />
      <Navigation links={linksAfterAnchor} collapseSideBar={collapseSideBar} />
      <div className="sidebar-spacer" />
      <Navigation links={sharedBottomLinks} collapseSideBar={collapseSideBar} />
    </>
  );
};

export default RealtorSidebarNav;
